import { useEffect, useMemo, useState } from "react"

import { LIMIT_WORD_COUNT } from "src/components/hooks/useTieup"
import { useLoading } from "../useLoading"
import getAllPlatesFromDB from "src/utils/editor/getAllplatesFromDB"
import { updateNovelStatus } from "src/models/tieup/novel/updateNovelStatus"
import { callDeleteChapterRawData } from "src/api/novel/data/delete"
import { deleteChapterPlate } from "src/models/tieup/novel/chapter/deleteChapterPlate"
import { deleteChapter as deleteChapterFn } from "src/models/tieup/novel/chapter/deleteChapter"
import { useTieupToast } from "../useTieupToast"
import { Chapter } from "src/models/tieup/novel/Chapter"
import { useNovel } from "./useNovel"
import { useCommonConfirmDialog } from "../useCommonConfirmDialog"
import { EditorMessage } from "src/config/messages"
import { NovelStatus } from "src/models/tieup/novel/NovelStatus"

const useNovelChapter = (
  novelId: number | null,
  isGuest = true,
  chapterId?: number | null,
) => {
  /** sort_orderでソートしたチャプター一覧配列 */
  const [chapters, setChapters] = useState<Chapter[]>([])

  const { novel, mutateNovel } = useNovel(
    novelId,
    isGuest ? "guestNovel" : "myPageNovel",
  )
  const { confirmDialog } = useCommonConfirmDialog()
  const { fullScreenLoading } = useLoading()
  const { addToastInfo } = useTieupToast()

  useEffect(() => {
    if (novel && novel.chapters) {
      const sortedChapters = novel.chapters.sort((a: Chapter, b: Chapter) => {
        if (a.id < b.id) return -1
        if (a.id > b.id) return 1

        return 0
      })
      setChapters(sortedChapters)
    }
  }, [novel])

  /** 公開状態にある chapter のみ抽出した配列 */
  const publishedChapters = useMemo(() => {
    return chapters.filter((item) => item.is_published)
  }, [chapters])

  /** 現在の chapter データ */
  const currentChapter = useMemo(() => {
    return chapters.find((item) => item.id === chapterId)
  }, [chapterId, chapters])

  /** 総文字数 */
  const totalWordCount = useMemo(() => {
    if (!chapters) return 0

    return chapters.reduce((accumulator, current) => {
      return accumulator + current.word_count
    }, 0)
  }, [chapters])

  /** 最新章 */
  const latestChapter = useMemo(() => {
    if (!chapters) return undefined

    return chapters[0]
  }, [chapters])

  /** 最終更新日時 */
  const latestUpdated = useMemo(() => {
    if (!latestChapter) return null

    const latestChapterCreated = latestChapter.created_at
    const latestChapterUpdated = latestChapter.updated_at

    // 最新話の更新日時、なければ作成日時
    return latestChapterUpdated ? latestChapterUpdated : latestChapterCreated
  }, [latestChapter])

  /** ソート順の章 */
  const sortedChapters = useMemo(() => {
    if (!publishedChapters) return []

    return publishedChapters.sort((a: Chapter, b: Chapter) => {
      if (a.sort_order > b.sort_order) return 1
      if (a.sort_order < b.sort_order) return -1

      return 0
    })
  }, [publishedChapters])

  /** 最終章 */
  const lastPublishedChapter = useMemo(() => {
    if (!sortedChapters) return null

    return sortedChapters[sortedChapters.length - 1]
  }, [sortedChapters])

  // 差分の図版をひとつずつ削除する
  const deletePlates = async (chapterId: number) => {
    // 章に紐づく図版リストを取得
    const targetPlates = await getAllPlatesFromDB(novelId, chapterId)
    const plateIds = targetPlates.map((item) => item.id)

    if (!novelId) {
      throw new Error(EditorMessage.error.common)
    }

    // 1枚ずつ削除
    await Promise.all(
      plateIds.map(
        async (plateId: number) =>
          await deleteChapterPlate({
            plateId,
            novelId,
            chapterId,
          }).catch((error) => {
            // eslint-disable-next-line no-console
            console.error(error)
          }),
      ),
    )
  }

  const deleteChapter = (
    { id: chapterId, title }: Chapter,
    callback?: () => void,
  ) => {
    if (!novelId) return

    const func = async () => {
      try {
        fullScreenLoading(true)
        await callDeleteChapterRawData(chapterId)
        await deleteChapterFn({ novelId, chapterId })
        await deletePlates(chapterId)
        if (callback) callback()
        addToastInfo(`${title}を削除しました。`)
        mutateNovel()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error)
      } finally {
        fullScreenLoading(false)
      }
    }
    confirmDialog(`${title}を削除しますか？`, "はい", "いいえ", func)
  }

  /** 章削除時の公開条件チェック（文字数） */
  useEffect(() => {
    async function changeStatus() {
      if (!novel) return
      if (
        novel.isTieup &&
        novel.status === 7 &&
        totalWordCount < LIMIT_WORD_COUNT // 文字数が公開条件に満たなかったら公開NG
      ) {
        if (!novelId) return
        try {
          await updateNovelStatus(novelId, NovelStatus.DRAFT)
          addToastInfo(
            `公開条件を満たしていないので、作品を非公開にしました。再度公開するためには、本文を${LIMIT_WORD_COUNT}文字以上登録し、作品公開をしてください。`,
          )
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error)
        }
      }
    }
    changeStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalWordCount])

  /**
   * まとめて削除する
   * @param chapters
   */
  const deleteChapters = (chapters: Chapter[]) => {
    // Confirmは呼び出し側でやって
    if (!novelId) return

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        for (const chapter of chapters) {
          await callDeleteChapterRawData(chapter.id)
          await deleteChapterFn({ novelId, chapterId: chapter.id })
          await deletePlates(chapter.id)
        }
        resolve("")
      } catch (error) {
        reject(error)
      }
    })
  }

  return {
    novel: novel,
    chapters,
    publishedChapters,
    deleteChapter,
    deleteChapters,
    deletePlates,
    currentChapter,
    latestChapter,
    latestUpdated,
    lastPublishedChapter,
    sortedChapters,
    totalWordCount,
    mutateNovel,
  }
}

export { useNovelChapter }
